import { BulbTwoTone, CheckCircleTwoTone, QuestionCircleTwoTone } from '@ant-design/icons'
import { Card, Checkbox, Collapse, Layout, List, Progress, Space, Tag, Typography } from 'antd'
import { Header, Content } from 'antd/es/layout/layout'
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { HeaderMenu } from '../../components/header-menu'
import { problemSolvingChecklistItems as checklistItems } from './adulting-101-checklists'

const { Title, Paragraph } = Typography

const faqItems = [
  {
    question: 'How do I know what the real problem is?',
    answer: (
      <>
        Ask yourself: "What's actually wrong, not just what I see on the surface?" For example, if you're failing a class, the problem might
        not be "I'm bad at math" but "I'm getting distracted" or "I'm not studying effectively." Being honest about the real cause connects
        to <Link to="/adulting-101/responsibility">Responsibility</Link> — owning the actual issue instead of an excuse.
      </>
    ),
  },
  {
    question: 'Why should I break a big problem into smaller parts?',
    answer: (
      <>
        A huge problem can feel paralyzing, but small pieces feel manageable. This is the same technique used in{' '}
        <Link to="/adulting-101/time-management">Time Management</Link> when you break a big task into a to-do list so it doesn't feel
        overwhelming.
      </>
    ),
  },
  {
    question: "What if I can't think of any solutions?",
    answer: (
      <>
        Talk to someone — a friend, parent, teacher, or mentor. Sometimes just explaining the problem out loud to someone in your{' '}
        <Link to="/adulting-101/friends-and-family">Friends and Family</Link> network helps your brain come up with ideas you couldn't see
        alone.
      </>
    ),
  },
  {
    question: 'Why should I think through consequences before acting?',
    answer: (
      <>
        Jumping straight to a solution without thinking it through can create new problems. Pausing to weigh the outcomes is the same
        self-control described in <Link to="/adulting-101/emotional-intelligence">Emotional Intelligence</Link> — not reacting on impulse.
      </>
    ),
  },
  {
    question: 'When should I ask for help instead of solving it myself?',
    answer: (
      <>
        Ask for help when you've tried your best and are stuck, or when someone else has expertise you don't. Asking for help isn't weakness
        — it's smart, the same way asking a parent about money questions is smart in{' '}
        <Link to="/adulting-101/financial-literacy">Financial Literacy</Link>.
      </>
    ),
  },
  {
    question: 'How do I know which solution to try first?',
    answer: (
      <>
        Think about which solution is most likely to work, easiest to try, and has the fewest risks if it fails. You don't need the perfect
        solution, just a good one to start with — the same mindset that helps with{' '}
        <Link to="/adulting-101/household-care">Household Care</Link> when you're figuring out how to tackle a messy space.
      </>
    ),
  },
  {
    question: 'What if I try something and it makes things worse?',
    answer: (
      <>
        That's a learning opportunity, not a failure. Ask yourself "why didn't it work?" and use that to try a different approach. Some of
        the best problem-solvers have failed many times — they just kept adjusting, similar to reviewing your day in{' '}
        <Link to="/adulting-101/time-management">Time Management</Link>.
      </>
    ),
  },
  {
    question: 'How do I stay calm when a problem is really stressful?',
    answer: (
      <>
        Take a breath, step away for a minute if you can, and remind yourself that panicking won't help you think clearly. Staying
        level-headed under pressure is a core part of <Link to="/adulting-101/emotional-intelligence">Emotional Intelligence</Link>.
      </>
    ),
  },
]

export function Adulting101ProblemSolvingPage() {
  const [completedIds, setCompletedIds] = useState<Set<string>>(() => new Set())
  const completedCount = completedIds.size
  const completionPercent = Math.round((completedCount / checklistItems.length) * 100)

  return (
    <Layout>
      <Header>
        <h1>Problem Solving</h1>
      </Header>
      <HeaderMenu selected="adulting-101" />
      <Content style={{ padding: '20px', textAlign: 'left', background: 'linear-gradient(180deg, #fffbe6 0%, #ffffff 220px)' }}>
        <Title level={2}>Problems are a normal part of life. The skill is knowing how to solve them.</Title>
        <Paragraph type="secondary">
          Good problem-solvers break things down, think through options, and aren't afraid to adjust when something doesn't work. These
          skills will help you handle challenges at school, work, and in relationships.
        </Paragraph>

        <Card
          title={
            <Space>
              <BulbTwoTone />
              Problem Solving Checklist
            </Space>
          }
          style={{ marginBottom: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}
          extra={<Progress type="circle" percent={completionPercent} size={56} />}
        >
          <List
            dataSource={checklistItems}
            renderItem={(item) => (
              <List.Item>
                <Checkbox
                  checked={completedIds.has(item.id)}
                  onChange={(event) => {
                    setCompletedIds((currentIds) => {
                      const nextIds = new Set(currentIds)
                      if (event.target.checked) {
                        nextIds.add(item.id)
                      } else {
                        nextIds.delete(item.id)
                      }
                      return nextIds
                    })
                  }}
                >
                  {item.text}
                </Checkbox>
              </List.Item>
            )}
          />
          {completionPercent === 100 ? (
            <Tag color="success" icon={<CheckCircleTwoTone twoToneColor="#52c41a" />} style={{ marginTop: '12px' }}>
              Congratulations, you're thinking like a problem-solver!
            </Tag>
          ) : (
            <Paragraph style={{ marginTop: '12px' }}>
              Every problem you solve makes you better at solving the next one. Good problem-solving skills open doors in school, work, and
              life.
            </Paragraph>
          )}
        </Card>

        <Card
          title={
            <Space>
              <QuestionCircleTwoTone />
              FAQ: Problem Solving
            </Space>
          }
          style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}
        >
          <Collapse
            items={faqItems.map((faq, index) => ({
              key: index,
              label: faq.question,
              children: <Paragraph>{faq.answer}</Paragraph>,
            }))}
          />
        </Card>
      </Content>
    </Layout>
  )
}
